import React, { useState, memo } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const UseCaseCard = memo(function UseCaseCard({ icon, title, description, examples, index }) {
  const [isFlipped, setIsFlipped] = useState(false);

  const imageMap = {
    "Real Estate":
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop",
    Healthcare:
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&h=300&fit=crop",
    Restaurants:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop",
    "Fitness & Gyms":
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop",
    "Retail Stores":
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop",
    "Salons & Spas":
      "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=300&fit=crop",
    Education:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop",
  };

  const imagePath =
    imageMap[title] ||
    "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=300&fit=crop";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: false, margin: "-50px", amount: 0.3 }}
      transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
        delay: index * 0.08,
      }}
      className="h-[200px] sm:h-[260px] md:h-[320px] perspective-1000"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onTouchStart={() => setIsFlipped(true)}
      onTouchEnd={() => setIsFlipped(false)}
    >
      <div
        className="relative w-full h-full transition-transform duration-700 preserve-3d"
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front Side */}
        <div
          className="absolute inset-0 backface-hidden rounded-xl overflow-hidden border border-white/10 group"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="relative w-full h-full">
            <img
              src={imagePath}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B2E] via-[#1A1B2E]/70 to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-end p-4 md:p-6">
              <div className="w-10 md:w-14 h-10 md:h-14 rounded-lg bg-[#E5B978]/20 backdrop-blur-sm flex items-center justify-center mb-2 md:mb-4 text-[#E5B978] border border-[#E5B978]/30">
                {icon}
              </div>
              <h3 className="text-sm md:text-xl font-bold text-white text-center">
                {title}
              </h3>
              <p className="text-[10px] md:text-xs text-[#E5B978] mt-1 md:mt-2 text-center">
                Tap to see details
              </p>
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 backface-hidden rounded-xl overflow-hidden border border-[#E5B978]/50 shadow-lg shadow-[#E5B978]/20"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div className="relative w-full h-full">
            <img
              src={imagePath}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#252640]/85 to-[#1A1B2E]/70" />
            <div className="absolute inset-0 p-3 md:p-6 flex flex-col">
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
                <div className="w-8 md:w-10 h-8 md:h-10 rounded-lg bg-[#E5B978]/20 flex items-center justify-center text-[#E5B978]">
                  {icon}
                </div>
                <h3 className="text-sm md:text-lg font-bold text-white">
                  {title}
                </h3>
              </div>
              <p className="text-white/80 text-[10px] md:text-sm mb-2 md:mb-4 leading-relaxed line-clamp-2 md:line-clamp-none">
                {description}
              </p>
              <ul className="space-y-1 md:space-y-2">
                {examples.map((example, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-[10px] md:text-xs text-white/70"
                  >
                    <Check className="w-3 h-3 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>{example}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

export default UseCaseCard;
